import React, { useCallback, useMemo, useRef, useState } from 'react';
import NiceModal from '@ebay/nice-modal-react';
import styled from 'styled-components';

import TrendChart from 'components/Intelligence/TrendChart';
import useMeasuredWidth from 'components/Intelligence/useMeasuredWidth';
import { IntelligenceThemeScope } from 'containers/Intelligence/theme';
import useMultiLanguageTranslation from 'hooks/useMultiLanguageTranslation';

import { useOrbitThemeVariant } from '../OrbitThemeScope';
import {
  OrbitChartModel,
  OrbitPlottedPoint,
  buildValueFormatter,
  formatMetricValue,
  isMeasured
} from '../chartAdapters';
import { downloadOrbitChartImage } from '../chartExport';
import { TOrbit } from '../i18n/TModules';
import { OrbitAnswerLabel, orbitColorsFor, orbitTokens, orbitType } from '../styles';
import OrbitChartActions from './OrbitChartActions';
import OrbitChartExpandModal from './OrbitChartExpandModal';
import OrbitDriverBars from './OrbitDriverBars';

export interface IOrbitChartProps {
  model: OrbitChartModel;
  /**
   * Draws the chart's own controls. The expanded copy inside `OrbitChartExpandModal` passes
   * `false`, so it cannot offer a second expand of itself.
   */
  interactive?: boolean;
}

// TODO(AF-10): the line answer is still drawn in no frame, so its height stays invented from the
// panel tokens. The donut (§7b M6) and the driver bars (§7c L3) are both fully specced.
const FALLBACK_WIDTH = 286;
const LINE_HEIGHT = 120;

const DONUT_OUTER = 88;
const DONUT_INNER = 62;

// Positioning context for the action pair, which v3 anchors to the chart row's own bottom-right
// corner (`5032:10910` at 399,469 inside a row ending at 451,489).
const Root = styled.div`
  position: relative;
  width: 100%;
`;

/**
 * What the download rasterises. The action pair is a SIBLING of this box rather than a child, so the
 * two icons overlay the chart on screen and stay out of the exported image.
 */
const Capture = styled.div`
  width: 100%;

  > * + * {
    margin-top: 6px;
  }
`;

/**
 * What the picture is OF. The bar variant draws its own title node (`4952:8096`), so this is only
 * for the two kinds that have none — and since the download rasterises `Capture`, a donut or trend
 * PNG left the panel with nothing in it naming the plotted metric (PLA-9563 §6a).
 */
const Caption = styled(OrbitAnswerLabel)`
  display: block;
`;

/**
 * div.donut-row 4952:8541 / `5032:10878`: the ring and its legend share a filled, rounded row
 * inside the answer.
 *
 * The bottom 29 is the action band, not a loose inset. `5032:10878` is 137 tall over an 88 ring:
 * 20 above it, then the ring to 460, then the download/expand pair at 469-485 (`5032:10910`,
 * `5032:10912`) and 4 to the row's own bottom at 489. At the 14 this carried the row closed at 122
 * and the pair — absolutely placed on the chart root at bottom 4 — landed ON the legend's last
 * line (legend bottom 802, icons top 800 at 1440x900, recheck20). 9 + 16 + 4 puts the band back.
 */
const DonutRow = styled.div`
  display: flex;
  align-items: center;
  padding: 20px 14px 29px;
  border-radius: 10px;
  background: ${orbitTokens.surfaceAlt};
`;

/**
 * A CSS conic ring, not components/Intelligence/DonutChart. That component takes `size` but derives
 * its hole from a module-level 34/47 ratio, which at an 88 outer gives a 64 hole where the design
 * asks for 62; its centre stack draws the value above the caption where the design draws TOTAL
 * above the value, at Intelligence theme type sizes rather than 8 Regular over 14 Bold; and it
 * fills the hole with surface-subpanel #141A2B, not the row's #101830. None of the four is a prop,
 * so matching the design through it would mean editing a component the Intelligence routes share.
 */
const Ring = styled.div<{ $stops: string }>`
  position: relative;
  display: flex;
  align-items: center;
  justify-content: center;
  flex: none;
  width: ${DONUT_OUTER}px;
  height: ${DONUT_OUTER}px;
  border-radius: 50%;
  background: conic-gradient(${({ $stops }) => $stops});
`;

const RingCentre = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  width: ${DONUT_INNER}px;
  height: ${DONUT_INNER}px;
  border-radius: 50%;
  background: ${orbitTokens.surfaceAlt};
`;

const CentreLabel = styled.span`
  color: ${orbitTokens.textMuted};
  ${orbitType.helping}
  text-transform: uppercase;
`;

const CentreValue = styled.strong`
  color: ${orbitTokens.text};
  font-size: 14px;
  font-weight: 700;
  line-height: 17px;
`;

/* Safari < 14.1 has no flex gap (conventions/components/cross-browser.md § 5). */
const Legend = styled.ul`
  flex: 1 1 auto;
  min-width: 0;
  margin: 0 0 0 16px;
  padding: 0;
  list-style: none;
`;

const LegendItem = styled.li`
  display: flex;
  align-items: center;
  padding: 3px 0;
  color: ${orbitTokens.textSecondary};
  ${orbitType.chip}
`;

const LegendDot = styled.span<{ $color: string }>`
  flex: none;
  width: 8px;
  height: 8px;
  margin-right: 7px;
  border-radius: 4px;
  background: ${({ $color }) => $color};
`;

const LegendValue = styled.span`
  margin-left: auto;
  color: ${orbitTokens.text};
  font-weight: 700;
  font-variant-numeric: tabular-nums;
`;

/**
 * Cumulative hard stops, so neighbouring segments meet with no blend. Returns null for a total of
 * zero: nothing was measured, and a ring painted in one flat colour would read as a full segment.
 */
const conicStops = (segments: OrbitPlottedPoint[]): string | null => {
  const plotted = segments.map(segment =>
    isMeasured(segment.value) && segment.value > 0 ? segment.value : 0
  );
  const total = plotted.reduce((sum, value) => sum + value, 0);

  if (total <= 0) {
    return null;
  }

  let cursor = 0;

  return segments
    .map((segment, index) => {
      const start = (cursor / total) * 100;
      cursor += plotted[index];

      return `${segment.color} ${start}% ${(cursor / total) * 100}%`;
    })
    .join(', ');
};

const OrbitChart: React.FC<IOrbitChartProps> = ({ model, interactive = true }) => {
  const { t } = useMultiLanguageTranslation();
  const rootRef = useRef<HTMLDivElement>(null);
  const captureRef = useRef<HTMLDivElement>(null);
  const [downloading, setDownloading] = useState(false);
  const themeVariant = useOrbitThemeVariant();
  const measured = useMeasuredWidth(rootRef);
  const width = measured > 0 ? measured : FALLBACK_WIDTH;

  // Tooltip only. The axis tick formatter stays the plain number formatter: recharts generates its
  // ticks from the domain, so a tick that collides with a reading would otherwise be labelled with
  // that reading's cohort size (`chartAdapters.buildValueFormatter`).
  const formatValue = useMemo(
    () =>
      buildValueFormatter(
        model.points,
        count => t(`${TOrbit}.metrics.customers`, { count }),
        model.format,
        model.currency
      ),
    [model, t]
  );
  const formatAxisValue = useMemo(
    () =>
      (value: number): string =>
        formatMetricValue(value, model.format, model.currency),
    [model]
  );

  const handleExpand = useCallback(() => {
    NiceModal.show(OrbitChartExpandModal, { model }).catch(() => undefined);
  }, [model]);

  const handleDownload = useCallback(() => {
    const node = captureRef.current;

    if (node === null) {
      return;
    }

    setDownloading(true);
    // The literal, not `orbitTokens.surface`: html-to-image paints the canvas outside any DOM that
    // could substitute a custom property.
    downloadOrbitChartImage(node, model.title, orbitColorsFor(themeVariant).surface).then(
      () => setDownloading(false),
      () => setDownloading(false)
    );
  }, [model, themeVariant]);

  const renderChart = (): React.ReactNode => {
    switch (model.kind) {
      case 'line':
        return (
          <TrendChart
            series={model.series}
            pointLabels={model.pointLabels}
            width={width}
            height={LINE_HEIGHT}
            fluid
            showAxes
            showLegend={model.series.length > 1}
            formatValue={formatValue}
            formatAxisValue={formatAxisValue}
          />
        );
      case 'donut': {
        const stops = conicStops(model.segments);

        return (
          <DonutRow>
            {stops !== null ? (
              <Ring $stops={stops} data-testid="orbit-chart-donut">
                <RingCentre>
                  <CentreLabel>{t(`${TOrbit}.metrics.total`)}</CentreLabel>
                  <CentreValue data-testid="orbit-chart-donut-value">
                    {model.centerValue}
                  </CentreValue>
                </RingCentre>
              </Ring>
            ) : null}
            <Legend data-testid="orbit-chart-legend">
              {model.segments.map(segment => (
                <LegendItem key={segment.label} data-testid="orbit-chart-legend-item">
                  <LegendDot
                    $color={segment.color}
                    aria-hidden="true"
                    data-testid="orbit-chart-legend-dot"
                  />
                  <span>{segment.label}</span>
                  <LegendValue>{segment.display}</LegendValue>
                </LegendItem>
              ))}
            </Legend>
          </DonutRow>
        );
      }
      case 'bar':
        return <OrbitDriverBars bars={model.columns} title={model.title} />;
      default:
        return null;
    }
  };

  // The shipped charts colour themselves through `var(--is-*)`, which only the Intelligence theme
  // scope declares; the Orbit panel sits outside every Intelligence route.
  return (
    <Root ref={rootRef} data-testid="orbit-chart" data-kind={model.kind}>
      <Capture ref={captureRef} data-testid="orbit-chart-capture">
        {model.kind !== 'bar' && model.title !== '' ? (
          <Caption data-testid="orbit-chart-caption">{model.title}</Caption>
        ) : null}
        <IntelligenceThemeScope>{renderChart()}</IntelligenceThemeScope>
      </Capture>
      {interactive ? (
        <OrbitChartActions
          onDownload={handleDownload}
          onExpand={handleExpand}
          downloading={downloading}
        />
      ) : null}
    </Root>
  );
};

export default OrbitChart;
